/**
 * V2 admin dashboard — the Stats revamp (user ask 2026-07-08).
 *
 * Same data sources as V1's admin-dashboard.js (fast-count RPCs with
 * pagination fallbacks), redesigned in the atlas language: KPI stamps
 * with sparklines, one layered activity chart instead of two bar walls,
 * a retention gauge, ink-bar leaderboards, category-health delta chips,
 * and the API thermometer as a horizontal gauge. All SVG is hand-rolled
 * (charts.js) — no chart library, no new CDN global.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, getTimeAgo } from '../../../assets/js/utils.js';
import { fetchAllCategories, categorySlug } from '../../core/categories.js';
import {
  loadOverview, getOverview, loadTopUsers, loadTopCategories, loadTopLocations,
  loadActivitySeries, loadLoginSeries, loadRecentYoinks, loadRetention,
  loadCategoryHealth, loadApiUsage,
} from './data.js';
import { areaChart, sparkline, gauge, legendSwatch, CHART_COLORS } from './charts.js';
import { estimateMonthCosts, formatUsd } from './api-costs.js';

const num = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

/** Sum helper that tolerates empty series. */
function sum(values) {
  return values.reduce((a, b) => a + b, 0);
}

/**
 * Week-over-week movement from a 14-day series: last 7 vs first 7.
 * Exported for tests.
 */
export function weekDelta(series) {
  if (series.length < 14) return null;
  const counts = series.map((d) => d.count);
  const lastWeek = sum(counts.slice(0, 7));
  const thisWeek = sum(counts.slice(7));
  return { thisWeek, lastWeek, change: thisWeek - lastWeek };
}

/** Busiest day of a series (null when everything is zero). Exported for tests. */
export function busiestDay(series) {
  if (!series.length) return null;
  const best = series.reduce((a, b) => (b.count > a.count ? b : a));
  return best.count > 0 ? best : null;
}

function deltaChip(change) {
  if (change > 0) return `<span class="admin-delta admin-delta-up">▲ +${num(change)}</span>`;
  if (change < 0) return `<span class="admin-delta admin-delta-down">▼ ${num(change)}</span>`;
  return '<span class="admin-delta admin-delta-flat">— 0</span>';
}

function kpiCard({ value, label, sub, spark, accent }) {
  return `
    <div class="admin-kpi ${accent ? `admin-kpi-${accent}` : ''}">
      <span class="admin-kpi-value">${value}</span>
      <span class="admin-kpi-label">${label}</span>
      ${sub ? `<span class="admin-kpi-sub">${sub}</span>` : ''}
      ${spark || ''}
    </div>`;
}

function boardCard(title, rows) {
  if (!rows.length) return `
    <div class="admin-card admin-board">
      <h3 class="admin-card-title">${title}</h3>
      <p class="admin-muted">Nothing logged yet.</p>
    </div>`;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return `
    <div class="admin-card admin-board">
      <h3 class="admin-card-title">${title}</h3>
      ${rows.map((r, i) => `
        <button type="button" class="admin-board-row" ${r.nav || ''}>
          <span class="admin-board-rank">${r.medal ? (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`) : `${i + 1}.`}</span>
          <span class="admin-board-name">
            <span class="admin-board-bar" style="width:${Math.round((r.count / max) * 100)}%"></span>
            <span class="admin-board-text">${r.html}</span>
          </span>
          <span class="admin-board-count">${num(r.count)}</span>
        </button>`).join('')}
    </div>`;
}

/**
 * Month-to-date Google spend table (exported for tests). Each proxy action
 * maps to the Google SKU its field mask bills (api-costs.js); the cap bar
 * shows how much of that SKU's monthly free calls are used. Amber past 80%,
 * red once billable calls cost real money.
 */
export function apiCostsHtml(month) {
  const { rows, totalCost } = estimateMonthCosts(month);
  if (!rows.length) return '';
  return `
    <h4 class="admin-cost-title">💸 Google month-to-date
      <span class="admin-cost-total ${totalCost > 0 ? 'admin-cost-total-charged' : ''}">${formatUsd(totalCost)} est.</span>
    </h4>
    <table class="admin-cost-table">
      <thead><tr><th>Endpoint</th><th>Billable</th><th>Free cap</th><th>Est. cost</th></tr></thead>
      <tbody>
        ${rows.map((r) => {
          const pct = Math.round(r.capUsed * 100);
          const state = r.cost > 0 ? 'over' : (r.capUsed >= 0.8 ? 'warn' : 'ok');
          return `
          <tr class="admin-cost-row-${state}">
            <td><strong>${escapeHtml(r.label)}</strong><span class="admin-cost-tier">${escapeHtml(r.tier)} · $${r.price}/1k</span></td>
            <td>${num(r.billable)}<span class="admin-cost-tier">of ${num(r.requests)} req</span></td>
            <td>
              <span class="admin-cost-cap-track"><span class="admin-cost-cap-fill" style="width:${Math.min(100, pct)}%"></span></span>
              <span class="admin-cost-tier">${pct}% of ${num(r.free)}</span>
            </td>
            <td class="admin-cost-amount">${formatUsd(r.cost)}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
    <p class="admin-axis-note">Estimated from proxy counters (cache misses × list price past each SKU’s monthly free calls). Google’s invoice in Cloud Console → Billing is the source of truth.</p>`;
}

function apiHealthHtml(api) {
  if (!api || api.error) {
    return `
      <div class="admin-card admin-card-span">
        <h3 class="admin-card-title">🌡️ API health</h3>
        <p class="admin-muted">${api?.error ? `Couldn’t read usage stats: ${escapeHtml(api.error)}${api.details ? ' — ' + escapeHtml(api.details) : ''}` : 'Loading…'}</p>
      </div>`;
  }
  const statusColor = { cool: CHART_COLORS.green, warm: '#b45309', hot: CHART_COLORS.red, fever: CHART_COLORS.red };
  const color = statusColor[api.status] || CHART_COLORS.green;
  const temp = Math.max(2, Math.min(100, api.temperature));
  const hourly = api.history?.hourly || [];
  const maxReq = Math.max(...hourly.map((h) => h.requests), 1);
  const hitRate = api.current.hourly.requests > 0
    ? Math.round((api.current.hourly.cache_hits / api.current.hourly.requests) * 100) : 0;
  return `
    <div class="admin-card admin-card-span">
      <h3 class="admin-card-title">🌡️ API health
        <span class="admin-status-pill" style="background:${color}">${escapeHtml(api.status)} · ${api.temperature}°</span>
      </h3>
      <div class="admin-temp-track"><div class="admin-temp-fill" style="width:${temp}%;background:${color}"></div></div>
      <p class="admin-muted">${escapeHtml(api.status_label || '')}</p>
      <div class="admin-mini-stats">
        <div class="admin-mini-stat"><strong>${num(api.current.hourly.requests)}</strong><span>this hour / ${num(api.current.limits.hourly)}</span></div>
        <div class="admin-mini-stat"><strong>${num(api.current.daily.requests)}</strong><span>today / ${num(api.current.limits.daily)}</span></div>
        <div class="admin-mini-stat"><strong>${hitRate}%</strong><span>cache hit rate</span></div>
        <div class="admin-mini-stat ${api.current.hourly.rate_limited > 0 ? 'admin-mini-stat-bad' : ''}"><strong>${num(api.current.hourly.rate_limited)}</strong><span>rate limited</span></div>
      </div>
      ${hourly.length ? `
        <div class="admin-hour-bars" aria-label="Requests over the last 24 hours">
          ${hourly.map((h) => `
            <span class="admin-hour-bar" title="${escapeHtml(h.label)}: ${h.requests} requests"
              style="height:${Math.max(4, Math.round((h.requests / maxReq) * 100))}%;
                background:${h.rate_limited > 0 ? CHART_COLORS.red : color}"></span>`).join('')}
        </div>
        <p class="admin-axis-note">last 24 hours</p>` : ''}
      ${apiCostsHtml(api.month)}
    </div>`;
}

export function mount(el, ctx) {
  const { router } = ctx;
  let destroyed = false;

  el.innerHTML = '<div class="v2-loading">Tallying the ledgers…</div>';

  const loaders = Promise.all([
    getOverview() ? Promise.resolve(getOverview()) : loadOverview(),
    loadTopUsers(),
    loadTopCategories(),
    loadTopLocations(),
    loadActivitySeries(),
    loadLoginSeries(),
    loadRecentYoinks(),
    loadRetention(),
    loadCategoryHealth(),
    loadApiUsage(),
    fetchAllCategories().catch(() => []),
  ]);

  loaders.then(([overview, topUsers, topCats, topLocs, activity, logins,
    recent, retention, health, api, allCats]) => {
    if (destroyed) return;
    const catById = new Map(allCats.map((c) => [c.id, c]));

    const yoinksWow = weekDelta(activity);
    const best = busiestDay(activity);
    const kpis = [
      kpiCard({
        value: num(overview?.totalYoinks),
        label: 'Yoinks',
        sub: `${num(overview?.todayYoinks)} today${yoinksWow ? ` · ${yoinksWow.change >= 0 ? '+' : ''}${num(yoinksWow.change)} wk/wk` : ''}`,
        spark: sparkline(activity.map((d) => d.count), CHART_COLORS.green),
        accent: 'green',
      }),
      kpiCard({
        value: num(overview?.totalUsers),
        label: 'Explorers',
        sub: `${num(overview?.loggedInToday)} active today`,
        spark: sparkline(logins.map((d) => d.count), CHART_COLORS.blue),
        accent: 'blue',
      }),
      kpiCard({ value: num(overview?.totalLocations), label: 'Locations' }),
      kpiCard({ value: num(overview?.totalCategories), label: 'Categories' }),
    ].join('');

    const chart = areaChart(
      [
        { values: activity.map((d) => d.count), color: CHART_COLORS.green, label: 'yoinks' },
        { values: logins.map((d) => d.count), color: CHART_COLORS.blue, label: 'explorers' },
      ].filter((s) => s.values.length),
      activity.length ? activity : logins,
    );

    const retentionHtml = retention ? `
      <div class="admin-retention-flex">
        ${gauge(retention.retentionRate, 'came back')}
        <div class="admin-retention-facts">
          <div class="admin-mini-stat"><strong>${num(retention.thisWeekActive)}</strong><span>active this week</span></div>
          <div class="admin-mini-stat"><strong>${num(retention.retained)}</strong><span>returned</span></div>
          <div class="admin-mini-stat"><strong>${num(retention.resurfaced)}</strong><span>resurfaced</span></div>
          <div class="admin-mini-stat ${retention.lapsed > 0 ? 'admin-mini-stat-bad' : ''}"><strong>${num(retention.lapsed)}</strong><span>wandered off</span></div>
        </div>
      </div>
      <p class="admin-axis-note">retention = explorers active both weeks ÷ active last week</p>
    ` : '<p class="admin-muted">No sign-in history yet.</p>';

    const healthRows = health.filter((c) => c.thisWeek > 0 || c.lastWeek > 0);

    el.innerHTML = `
      <div class="admin-kpis">${kpis}</div>

      <div class="admin-card admin-card-span">
        <h3 class="admin-card-title">Expedition activity — last 14 days</h3>
        <div class="admin-legend">
          <span class="admin-legend-item">${legendSwatch(CHART_COLORS.green, 0)}Yoinks added</span>
          <span class="admin-legend-item">${legendSwatch(CHART_COLORS.blue, 1)}Unique explorers</span>
          ${best ? `<span class="admin-legend-note">busiest: ${escapeHtml(best.label)} · ${num(best.count)} yoinks</span>` : ''}
        </div>
        ${chart || '<p class="admin-muted">No activity yet.</p>'}
      </div>

      <div class="admin-two-up">
        <div class="admin-card">
          <h3 class="admin-card-title">🧭 Do they come back?</h3>
          ${retentionHtml}
        </div>
        <div class="admin-card">
          <h3 class="admin-card-title">📋 Latest yoinks</h3>
          ${recent.length ? recent.map((y) => {
            const who = y.public_profiles?.full_name || y.public_profiles?.username || 'Unknown';
            const emoji = y.categories?.emoji || '📍';
            const img = y.images?.length
              ? (y.images[0].startsWith('http') ? y.images[0] : gateway.getPublicUrl('yoinks', y.images[0]))
              : null;
            return `
              <button type="button" class="admin-recent" data-admin-yoink="${y.id}">
                ${img ? `<img class="admin-recent-thumb" src="${img}" alt="" loading="lazy">`
                  : `<span class="admin-recent-thumb admin-recent-emoji">${emoji}</span>`}
                <span class="admin-recent-body">
                  <span class="admin-recent-title">${escapeHtml(y.title)}</span>
                  <span class="admin-recent-meta">${escapeHtml(who)} · ${emoji} ${escapeHtml(y.categories?.name || 'Uncategorized')} · ${getTimeAgo(y.created_at)}</span>
                </span>
              </button>`;
          }).join('') : '<p class="admin-muted">No yoinks yet.</p>'}
        </div>
      </div>

      <div class="admin-boards">
        ${boardCard('🏆 Top collectors', topUsers.map((u) => ({
          count: u.count, medal: true,
          html: escapeHtml(u.name),
          nav: u.username ? `data-admin-user="${escapeHtml(u.username)}"` : '',
        })))}
        ${boardCard('📁 Top categories', topCats.map((c) => {
          const cat = catById.get(c.catId);
          return {
            count: c.count,
            html: `${c.emoji} ${escapeHtml(c.name)}`,
            nav: cat ? `data-admin-cat="${categorySlug(cat)}"` : '',
          };
        }))}
        ${boardCard('📍 Top locations', topLocs.map((l) => ({
          count: l.count,
          html: escapeHtml(l.name),
          nav: `data-admin-loc="${l.locId}"`,
        })))}
      </div>

      <div class="admin-card admin-card-span">
        <details class="admin-health">
          <summary class="admin-card-title">📈 Category health — this week vs last</summary>
          ${healthRows.length ? `
            <table class="admin-table">
              <thead><tr><th>Category</th><th class="admin-num">This wk</th><th class="admin-num">Last wk</th><th class="admin-num">Trend</th></tr></thead>
              <tbody>
                ${healthRows.map((c) => {
                  const cat = catById.get(c.id);
                  return `
                    <tr class="admin-row-link" ${cat ? `data-admin-cat="${categorySlug(cat)}"` : ''}>
                      <td>${c.emoji} ${escapeHtml(c.name)}</td>
                      <td class="admin-num"><strong>${num(c.thisWeek)}</strong></td>
                      <td class="admin-num">${num(c.lastWeek)}</td>
                      <td class="admin-num">${deltaChip(c.change)}</td>
                    </tr>`;
                }).join('')}
              </tbody>
            </table>` : '<p class="admin-muted">No category activity in the last two weeks.</p>'}
        </details>
      </div>

      ${apiHealthHtml(api)}
    `;
  }).catch(() => {
    if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load the dashboard.</div>';
  });

  function onClick(event) {
    const yoinkEl = event.target.closest('[data-admin-yoink]');
    if (yoinkEl) return router.navigate('yoink', { id: yoinkEl.dataset.adminYoink });
    const userEl = event.target.closest('[data-admin-user]');
    if (userEl) return router.navigate('user', { username: userEl.dataset.adminUser });
    const catEl = event.target.closest('[data-admin-cat]');
    if (catEl) return router.navigate('category', { slug: catEl.dataset.adminCat });
    const locEl = event.target.closest('[data-admin-loc]');
    if (locEl) return router.navigate('location', { id: locEl.dataset.adminLoc });
  }
  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
